import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Device, DeviceEntryProps, Firmware, FirmwareKind, FirmwareProps } from '@ixicare/resources';
import { Alert, Button, Spinner, formatDateTime } from '@ixicare/ui';
import { RefreshCw } from 'lucide-react';
import { t } from '~i18n';
import { useStores } from '~store/utils';
import { humaniseErrors } from '~utils/error';
import { OTA_TASKS_QUERY_KEY, otaTaskKind } from '../hooks/useOtaTasks';
import { plainVersion } from '../utils/firmwareLabels';
import { canRetryOtaTask, otaTaskState, otaTaskStateLabel } from '../utils/otaTaskState';
import styles from './DeviceFirmwareStatus.module.css';

interface Props {
  device: DeviceEntryProps;
  kind: FirmwareKind;
  latest?: FirmwareProps;
  /** Called on refresh, so the list the device was opened from shows the new version too. */
  onDeviceChanged?: () => void;
}

/** What one device runs of this kind and how its last update of it went, with retry and cancel. */
export const DeviceFirmwareStatus = ({ device, kind, latest, onDeviceChanged }: Props) => {
  const { notifications, confirmation } = useStores();
  const queryClient = useQueryClient();

  const tasksQuery = useQuery({
    queryKey: [...OTA_TASKS_QUERY_KEY, 'device', device.id],
    queryFn: () => Firmware.getOtaTasks({ deviceId: device.id }),
  });
  // The row the modal was opened from is a snapshot; the version changes once the device updated.
  const deviceQuery = useQuery({
    queryKey: ['firmwareDevice', device.id],
    queryFn: () => Device.getDevice(device.id),
    initialData: device,
  });
  const task = tasksQuery.data?.find((candidate) => otaTaskKind(candidate) === kind);
  const state = task && otaTaskState(task);
  // The firmware version is shown as reported: its format is not known to be a plain version.
  const current = kind === 'firmware' ? deviceQuery.data?.fwVersion : plainVersion(deviceQuery.data?.swVersion);

  // The device's own status and the badges in the table come from the same queries.
  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: OTA_TASKS_QUERY_KEY });
    deviceQuery.refetch();
    onDeviceChanged?.();
  };
  const isRefreshing = tasksQuery.isFetching || deviceQuery.isFetching;

  const onDone = (message: string) => {
    queryClient.invalidateQueries({ queryKey: OTA_TASKS_QUERY_KEY });
    notifications.addUserActionNotification({ type: 'success' as any, notificationType: 'success', message });
  };
  const retryMutation = useMutation({
    mutationFn: () => Firmware.retryOtaTask(task!.id),
    onSuccess: () => onDone(t('firmware.task.retried')),
  });
  const cancelMutation = useMutation({
    mutationFn: () => Firmware.cancelOtaTask(task!.id),
    onSuccess: () => onDone(t('firmware.task.cancelled')),
  });
  const error = retryMutation.error ?? cancelMutation.error;
  const busy = retryMutation.isPending || cancelMutation.isPending;

  const confirmCancel = () =>
    confirmation.show({
      title: t('firmware.task.cancelTitle'),
      message: task?.delivered ? t('firmware.task.cancelDeliveredMessage') : t('firmware.task.cancelMessage'),
      dangerous: true,
      onConfirm: () => cancelMutation.mutate(),
      onCancel: () => {},
    });

  return (
    <div className={styles.Status}>
      {error && <Alert type="error">{humaniseErrors(error as Error).title}</Alert>}
      <dl className={styles.Details}>
        <dt>{t('firmware.device.current')}</dt>
        <dd>
          {current ?? '—'}
          {!!latest && !!current && plainVersion(current) === plainVersion(latest.version) && (
            <span className={styles.UpToDate}>{t('firmware.device.upToDate')}</span>
          )}
        </dd>
        <dt className={styles.LastUpdateTitle}>
          {t('firmware.device.lastUpdate')}
          <button
            type="button"
            className={styles.Refresh}
            disabled={isRefreshing}
            onClick={refresh}
            aria-label={t('genericTable.refreshAria')}
            title={t('genericTable.refreshAria')}
          >
            <RefreshCw
              size={13}
              strokeWidth={2}
              className={isRefreshing ? styles.Spinning : undefined}
              aria-hidden="true"
            />
          </button>
        </dt>
        <dd>{tasksQuery.isLoading ? <Spinner /> : task ? otaTaskStateLabel(task) : t('firmware.device.noUpdates')}</dd>
        {task && state && (
          <>
            <dt />
            <dd className={styles.Muted}>
              {state === 'done' && kind === 'software'
                ? t('firmware.task.explanation.doneSoftware')
                : t(`firmware.task.explanation.${state}`)}
            </dd>
            {task.executionDatetime && (
              <>
                <dt>{t('firmware.task.plannedAt')}</dt>
                <dd>{formatDateTime(task.executionDatetime)}</dd>
              </>
            )}
            {task.errorCode && (
              <>
                <dt>{t('firmware.task.errorCode')}</dt>
                <dd>{task.errorCode}</dd>
              </>
            )}
            <dt>{t('firmware.task.lastChange')}</dt>
            <dd>{formatDateTime(task.updated)}</dd>
          </>
        )}
      </dl>
      {task && (canRetryOtaTask(task) || state !== 'done') && (
        <div className={styles.Actions}>
          {canRetryOtaTask(task) && (
            <Button
              type="button"
              disabled={busy}
              loading={retryMutation.isPending}
              onClick={() => retryMutation.mutate()}
            >
              {t('firmware.task.retry')}
            </Button>
          )}
          {state !== 'done' && (
            <Button type="button" buttonType="link" disabled={busy} onClick={confirmCancel}>
              {t('firmware.task.cancel')}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};
